import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import logoAlbaker from "@/assets/logo-albaker.png";

const ArticleDetail = () => {
  const { slug } = useParams();
  const { lang, isRTL } = useLanguage();
  const { user } = useAuth();

  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get(`/articles/${slug}`)
      .then((r) => {
        if (!mounted) return;
        setArticle(r.data);
        return api.get(`/comments/article/${r.data.id}`).then((c) => {
          if (mounted) setComments(c.data.items || []);
        });
      })
      .catch((e) => mounted && setError(formatApiError(e)))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!body.trim() || !article) return;
    setPosting(true);
    try {
      const { data } = await api.post("/comments", { article_id: article.id, body });
      setComments((c) => [data.comment, ...c]);
      setBody("");
      toast.success(isRTL ? "تم نشر تعليقك" : "Comment posted");
    } catch (err) {
      toast.error(formatApiError(err, isRTL ? "تعذّر نشر التعليق" : "Could not post comment"));
    } finally {
      setPosting(false);
    }
  };

  const t = isRTL
    ? { back: "الرئيسية", articles: "المقالات", comments: "التعليقات", writeComment: "اكتب تعليقًا", post: "نشر", posting: "جاري…", loginRequired: "سجّل الدخول للتعليق", commentsClosed: "التعليقات مغلقة على هذا المقال.", noComments: "لا توجد تعليقات بعد. كن الأول.", views: "مشاهدة" }
    : { back: "Home", articles: "Articles", comments: "Comments", writeComment: "Write a comment", post: "Post", posting: "Posting…", loginRequired: "Sign in to comment", commentsClosed: "Comments are closed for this article.", noComments: "No comments yet. Be the first.", views: "views" };

  if (loading) return <div className="min-h-screen bg-ink text-bone flex items-center justify-center"><span className="overline animate-pulse">…</span></div>;
  if (error) return <div className="min-h-screen bg-ink text-bone flex items-center justify-center text-red-400">{error}</div>;
  if (!article) return null;

  const localized = article[lang] || article.ar || article.en;

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="min-h-screen bg-ink text-bone">
      <Toaster position={isRTL ? "top-left" : "top-right"} theme="dark" richColors />

      <div className="container-x pt-20 pb-12">
        <div className="flex items-center justify-between mb-12">
          <Link to="/" className="inline-flex items-center gap-3"><img src={logoAlbaker} alt="" className="h-10 w-auto" /><span className="overline">{t.back}</span></Link>
          <Link to="/articles" className="overline hover:text-gold transition-colors">{t.articles}</Link>
        </div>
      </div>

      {article.cover_image && (
        <div className="relative w-full h-[55vh] overflow-hidden">
          <img src={article.cover_image} alt={localized.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/10" />
        </div>
      )}

      <article className="container-x py-16 max-w-3xl mx-auto">
        <div className="text-xs tracking-[0.25em] uppercase text-gold mb-6">
          {(article.publish_date || "").slice(0, 10)} · {article.views || 0} {t.views}
        </div>
        <h1 className={`${isRTL ? "font-arabic font-black leading-[1.15]" : "font-serif leading-[1.05]"} text-bone text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-10`}>
          {localized.title}
        </h1>
        {localized.excerpt && (
          <p className={`text-white/70 ${isRTL ? "font-arabic text-xl" : "text-lg"} leading-relaxed mb-12`}>
            {localized.excerpt}
          </p>
        )}

        {article.video_url && (
          <div className="mb-12 aspect-video bg-ink-soft border border-white/10">
            <iframe
              src={toEmbed(article.video_url)}
              title="video"
              className="w-full h-full"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        <div
          className={`prose-article ${isRTL ? "font-arabic text-lg leading-[2]" : "text-base leading-relaxed"} text-white/80`}
          dangerouslySetInnerHTML={{ __html: localized.body || "" }}
        />
      </article>

      {/* Comments */}
      <section className="container-x pb-24 max-w-3xl mx-auto">
        <div className="border-t border-white/10 pt-12">
          <div className="overline mb-8">{t.comments} ({comments.length})</div>

          {!article.comments_enabled ? (
            <div className="text-white/50 font-arabic">{t.commentsClosed}</div>
          ) : user ? (
            <form onSubmit={submitComment} className="mb-12" data-testid="comment-form">
              <textarea
                rows={4}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={t.writeComment}
                className="editorial-input resize-none"
                data-testid="comment-body"
                required
              />
              <button type="submit" disabled={posting} className="btn-gold mt-6 disabled:opacity-60" data-testid="comment-submit">
                {posting ? t.posting : t.post}
              </button>
            </form>
          ) : (
            <Link to="/login" className="btn-ghost mb-12 inline-flex">{t.loginRequired}</Link>
          )}

          <ul className="space-y-6">
            {comments.length === 0 && <li className="text-white/50">{t.noComments}</li>}
            {comments.map((c) => (
              <li key={c.id} className="border border-white/10 p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-bone font-semibold">{c.user_name || "—"}</span>
                  <span className="text-xs text-white/40">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <p className={`text-white/75 whitespace-pre-wrap ${isRTL ? "font-arabic" : ""}`}>{c.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

const toEmbed = (url) => {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
};

export default ArticleDetail;
